import {useEffect, useRef, useState} from 'react';
import {debounce, isEmpty} from 'lodash';

import {WEEKLY_CHECK_INS} from '/enum/clientJourneyTasks.enum';

import {
  getNewTasks,
  getTaskDataToSave,
  getTaskDataWithMeta,
  getTasksOnChange,
  getTasksWithLabel,
  getUpdatedTasks,
} from '../ClientJourneyTask.util';

/**
 * @param {object} params - Parameters
 * @param {Object[]} params.filteredTasks - an object that contains tile data (description)
 * @param {string}   params.filteredTasks.tabDescription
 * @param {string}   params.filteredTasks.screenTabCode
 * @param {Object[]} params.filteredTasks.checkBox
 * @param {string}   params.filteredTasks.checkBox.screenTabCode
 * @param {string}   params.filteredTasks.checkBox.chkBoxCode
 * @param {string}   params.filteredTasks.checkBox.description
 * @param {Object[]} params.weeklyCheckInsTasks - an object that contains data about the status of the checkbox
 * @param {number}   params.weeklyCheckInsTasks.clientId
 * @param {string}   params.weeklyCheckInsTasks.screenTabCode
 * @param {Object[]} params.weeklyCheckInsTasks.checkIns
 * @param {string}   params.weeklyCheckInsTasks.checkIns.chkBoxCode
 * @param {boolean}  params.weeklyCheckInsTasks.checkIns.checkStatus
 * @param {function} params.fetchClientJourneyTask - function for sending a post request to change the status of the checkbox
 * @param {function} params.setClientJourneyTasks - function for changing the status in the redux
 * @param {boolean}  params.hasWeeklyCheckInsTasks - the value for checking the presence of uploaded data. If the data has not been loaded returns false
 *
 * @returns {object} tasks - object for displaying data in a FlatList
 * @returns {function} getTaskItemIcon - return name of icon for tile: 'checkbox-selected' or 'checkbox-unselected'
 * @returns {function} onTaskStatusChange - function fur changing task status
 */
export const useWeeklyCheckIns = ({
  weeklyCheckInsInitialTasks,
  weeklyCheckInsTasks,
  fetchClientJourneyTask,
  setClientJourneyTasks,
  clientId,
  clientJourneyTimer,
  setClientJourneyState,
  lastCheckInDate,
}: any) => {
  const tasksRef = useRef([]);
  const timer = useRef(null);
  const [newWeeklyCheckInsTask, setNewWeeklyCheckInsTask] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [isCheckboxStatusChanged, setIsCheckboxStatusChanged] = useState(false);

  /**
   * @param {boolean} taskStatus
   */
  const getTaskItemIcon = (taskStatus: any) =>
    taskStatus ? 'checkbox-selected' : 'checkbox-unselected';

  const saveWeeklyTaskData = (data: any) => {
    // Send the request with new data
    fetchClientJourneyTask({
      task: getTaskDataWithMeta(newWeeklyCheckInsTask, data, WEEKLY_CHECK_INS),
    });
  };

  const onTaskStatusChange = (status: any, id: any) => {
    setIsCheckboxStatusChanged(true);
    const tasksAfterStatusChange = getTasksOnChange(tasks, id, status);
    setTasks(tasksAfterStatusChange);
    // Save it in redux to use the updated data and avoid delays due to the request
    setClientJourneyTasks({
      tasks: getUpdatedTasks(
        tasksAfterStatusChange,
        newWeeklyCheckInsTask,
        WEEKLY_CHECK_INS,
        lastCheckInDate,
      ),
    });
    // @ts-expect-error TS(2769): No overload matches this call.
    clearTimeout(timer.current);
  };

  useEffect(() => {
    setIsCheckboxStatusChanged(false);
    if (isCheckboxStatusChanged) {
      // @ts-expect-error TS(2322): Type 'Timeout' is not assignable to type 'null'.
      timer.current = setTimeout(() => {
        debounce(
          saveWeeklyTaskData,
          clientJourneyTimer,
        )(getTaskDataToSave(tasksRef?.current));
      }, clientJourneyTimer);
      // calling the client journey loading state to reload ClientsToContact nextTask filter
      setClientJourneyState();
    }
  }, [isCheckboxStatusChanged]);

  useEffect(() => {
    if (isEmpty(newWeeklyCheckInsTask) || isEmpty(weeklyCheckInsInitialTasks))
      return;

    const updatedTasks = getTasksWithLabel(
      newWeeklyCheckInsTask,
      weeklyCheckInsInitialTasks,
    );

    setTasks(prev => {
      if (JSON.stringify(prev) === JSON.stringify(updatedTasks)) {
        return prev;
      }
      return updatedTasks;
    });
  }, [newWeeklyCheckInsTask, weeklyCheckInsInitialTasks]);

  useEffect(() => {
    let newData;

    if (!isEmpty(weeklyCheckInsTasks)) {
      newData = weeklyCheckInsTasks;
    } else if (!isEmpty(weeklyCheckInsInitialTasks)) {
      newData = getNewTasks(clientId, weeklyCheckInsInitialTasks);
    }

    if (!newData) return;

    setNewWeeklyCheckInsTask(prev => {
      if (JSON.stringify(prev) === JSON.stringify(newData)) {
        return prev;
      }
      return newData;
    });
  }, [weeklyCheckInsTasks, weeklyCheckInsInitialTasks]);

  useEffect(() => {
    tasksRef.current = tasks;
  }, [tasks]);

  return {tasks, getTaskItemIcon, onTaskStatusChange};
};
